import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGS, LANG_LABELS, type Lang } from '@/types/lang';
import { startAnonSession } from '@/lib/auth/useAuth';
import { parseCode, isValidCode, formatCode } from '@/lib/family-code/crockford';

/**
 * Login — Family (Oturum 8 22:00 — yaşlı-dostu auth refactor).
 *
 * Begüm karar: email/Google/password tamamen kaldırıldı. Tek akış: ad + (varsa)
 * aile kodu. Arka planda Supabase anonymous sign-in kullanılır → mevcut RLS +
 * Edge Function altyapısı aynen çalışır, kullanıcı email görmez.
 *
 * 2-mode landing:
 *   - 'landing'   — 2 büyük CTA: "Yeni aile başlat" / "Aileme katıl"
 *   - 'join-code' — aile kodu input + submit
 *
 * Akış:
 *   1. Kullanıcı "Yeni aile başlat" → startAnonSession() → localStorage'a
 *      `family.onboardingMode='create'` yazılır → App.tsx Onboarding render eder
 *   2. Kullanıcı "Aileme katıl" → kod input ekranı → submit → startAnonSession() +
 *      `family.onboardingMode='join'` + `family.pendingInviteCode=<kod>` →
 *      App.tsx Onboarding'i join mode'da render eder
 *
 * Dil seçimi: Landing üstünde 4 dil chip (Oturum 7 kararı korunur) — Anne /
 * Mirsad amca için ana dili önceden seçme imkanı. Profile DB UPDATE yok
 * (session henüz yok); sadece i18n + localStorage.
 */

type Mode = 'landing' | 'join-code';

export function Login() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [mode, setMode] = useState<Mode>('landing');
  // Defensive prefill: WhatsApp davet linki ?code= App.tsx auto-handler ile
  // pendingInviteCode'a yazıldıysa, ve kullanıcı manuel "Aileme katıl" yoluna
  // düşerse (örn. auto anon session fail veya defensive yol), kod prefilled gelsin.
  const [familyCode, setFamilyCode] = useState<string>(() => {
    if (typeof localStorage === 'undefined') return '';
    return localStorage.getItem('family.pendingInviteCode') || '';
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  function pickLang(lang: Lang) {
    void i18n.changeLanguage(lang);
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('family.uiLang', lang);
      } catch {
        // ignore
      }
    }
  }

  async function handleStartNewFamily() {
    setErrorKey(null);
    setSubmitting(true);
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('family.onboardingMode', 'create');
        // Eski davet kodu varsa temizle (yeni aile başlatıyoruz)
        localStorage.removeItem('family.pendingInviteCode');
      } catch {
        // ignore
      }
    }
    const result = await startAnonSession();
    setSubmitting(false);
    if (!result.ok) {
      setErrorKey(result.error);
      return;
    }
    // Session set olduktan sonra App.tsx auth gate Onboarding'i mount eder
  }

  async function handleJoinFamilySubmit(e: FormEvent) {
    e.preventDefault();
    setErrorKey(null);
    const parsed = parseCode(familyCode);
    if (!parsed) {
      setErrorKey('familyCode.invalidFormat');
      return;
    }
    const formatted = formatCode(parsed);
    if (!isValidCode(formatted)) {
      setErrorKey('familyCode.invalidFormat');
      return;
    }
    setSubmitting(true);
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('family.onboardingMode', 'join');
        localStorage.setItem('family.pendingInviteCode', formatted);
      } catch {
        // ignore
      }
    }
    const result = await startAnonSession();
    setSubmitting(false);
    if (!result.ok) {
      setErrorKey(result.error);
      return;
    }
    // App.tsx Onboarding'i join mode'da render eder
  }

  if (mode === 'landing') {
    return (
      <main className="min-h-screen bg-bg-app text-text-primary font-sans flex flex-col items-center justify-center px-6 py-10 max-w-md mx-auto">
        {/* Dil chip bar (Oturum 7'den korunan) — Anne/Mirsad ana dilini önce seçer */}
        <div className="flex gap-2 mb-8" role="group" aria-label={t('settings.language')}>
          {LANGS.map((option) => {
            const active = option === currentLang;
            return (
              <button
                key={option}
                type="button"
                onClick={() => pickLang(option)}
                aria-pressed={active}
                className={`px-3 py-1.5 rounded-full text-xs font-serif italic border transition-colors duration-150 ${
                  active
                    ? 'bg-accent-primary text-white border-accent-gold shadow-warm-sm'
                    : 'bg-bg-elevated text-text-secondary border-border-subtle hover:border-accent-gold'
                }`}
              >
                {LANG_LABELS[option]}
              </button>
            );
          })}
        </div>

        {/* Editorial brand */}
        <h1 className="font-serif italic text-4xl text-text-primary mb-1">Family</h1>
        <div className="w-12 h-px bg-accent-gold mb-6" aria-hidden="true" />
        <p className="eyebrow mb-2">{t('auth.landingEyebrow')}</p>
        <h2 className="font-serif italic text-2xl text-accent-primary text-center mb-2">
          {t('auth.landingTitle')}
        </h2>
        <p className="text-base text-text-secondary text-center mb-10 font-serif italic">
          {t('auth.landingSubtitle')}
        </p>

        {/* 2 büyük CTA */}
        <div className="w-full flex flex-col gap-3">
          <button
            type="button"
            onClick={() => void handleStartNewFamily()}
            disabled={submitting}
            className="w-full min-h-tap py-4 rounded-2xl bg-accent-primary text-white font-serif italic text-lg shadow-warm-sm hover:opacity-90 active:scale-[0.99] disabled:opacity-50 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2"
          >
            {t('auth.startNewFamily')}
          </button>
          <button
            type="button"
            onClick={() => setMode('join-code')}
            disabled={submitting}
            className="w-full min-h-tap py-4 rounded-2xl bg-bg-elevated text-accent-gold-dark border border-accent-gold font-serif italic text-lg hover:bg-bg-bubble-received/40 active:scale-[0.99] disabled:opacity-50 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2"
          >
            {t('auth.joinFamily')}
          </button>
        </div>

        {errorKey ? (
          <p role="alert" className="mt-4 text-sm text-accent-warning text-center">
            {t(errorKey as 'errors.unknown')}
          </p>
        ) : null}

        <div className="mt-12 flex flex-col items-center text-xs text-text-secondary">
          <div className="ornament-divider w-24 mb-2" aria-hidden="true">
            <span>❀</span>
          </div>
          <p
            lang="en"
            className="font-serif italic"
            style={{ textTransform: 'none' }}
          >
            BOSNIA · TÜRKİYE · 2026
          </p>
        </div>
      </main>
    );
  }

  // mode === 'join-code'
  return (
    <main className="min-h-screen bg-bg-app text-text-primary font-sans flex flex-col items-center justify-center px-6 py-10 max-w-md mx-auto">
      <h1 className="font-serif italic text-3xl text-text-primary mb-2">Family</h1>
      <div className="w-12 h-px bg-accent-gold mb-6" aria-hidden="true" />
      <p className="eyebrow mb-2">{t('auth.joinEyebrow')}</p>
      <h2 className="font-serif italic text-2xl text-accent-primary text-center mb-2">
        {t('auth.codeInputLabel')}
      </h2>
      <p className="text-sm text-text-secondary text-center mb-8 font-serif italic">
        {t('auth.codeInputHint')}
      </p>

      <form
        onSubmit={(e) => {
          // iPhone Safari sync preventDefault (Oturum 9 04:30 mobile bug)
          e.preventDefault();
          void handleJoinFamilySubmit(e);
        }}
        className="w-full flex flex-col gap-3"
      >
        <input
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          value={familyCode}
          onChange={(e) => setFamilyCode(e.target.value.toUpperCase())}
          placeholder={t('auth.codeInputPlaceholder')}
          aria-label={t('auth.codeInputLabel')}
          className="w-full min-h-tap py-4 px-5 rounded-2xl bg-bg-elevated border border-border-strong text-center text-2xl font-mono tracking-wider focus:border-accent-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary"
          maxLength={14}
        />
        <button
          type="submit"
          disabled={submitting || familyCode.trim().length === 0}
          className="w-full min-h-tap py-4 rounded-2xl bg-accent-primary text-white font-serif italic text-lg shadow-warm-sm hover:opacity-90 active:scale-[0.99] disabled:opacity-50 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2"
        >
          {t('auth.joinSubmit')}
        </button>
        <button
          type="button"
          onClick={() => {
            setMode('landing');
            setFamilyCode('');
            setErrorKey(null);
          }}
          className="w-full min-h-tap py-3 text-sm text-accent-link gold-underline"
        >
          {t('auth.backToLanding')}
        </button>
      </form>

      {errorKey ? (
        <p role="alert" className="mt-4 text-sm text-accent-warning text-center">
          {t(errorKey as 'errors.unknown')}
        </p>
      ) : null}
    </main>
  );
}
